import type { Edge, Node, XYPosition } from '@xyflow/react';
import type { StepLayoutGroup, StepLayoutGroupColor } from '@erp/workflows';
import { getCategoryStyle } from './categoryStyles';

/**
 * Step layout groups: labelled, colourable boxes drawn behind a workflow's nodes.
 *
 * They are decoration and nothing else — no socket, no edge, no effect on
 * readiness or execution. The only thing they *do* is own their members as
 * React Flow subflow children, which buys native behaviour for free: dragging a
 * group moves everything inside it, and a member's position is stored relative
 * to its group so the whole region translates as a unit.
 *
 * The cost of that is one coordinate convention to keep straight:
 *
 *   - Persisted (`StepLayoutGroup.position`, `StepDefinition.position`) is always
 *     **workflow-local absolute** — no group offsets anywhere.
 *   - In the React Flow graph, a grouped step's `position` is **relative to its
 *     group**, because that's what `parentId` means.
 *
 * `buildStepLayoutGroupGraph` converts absolute → relative on load and
 * `extractStepLayoutGroups` / `toAbsoluteNodePositions` convert back on save. Every
 * other module should stay in the absolute world and never do the arithmetic
 * itself.
 */

export const STEP_LAYOUT_GROUP_NODE_TYPE = 'stepLayoutGroup';

/** Side and bottom breathing room between a group's edge and its members. */
export const STEP_LAYOUT_GROUP_PADDING = 40;
/**
 * Top breathing room. Larger than the rest because the label pill lives inside
 * the box's top-left corner — outside it (the `WorkflowFrameNode` treatment)
 * would collide with nodes sitting above the group on a dense canvas.
 */
export const STEP_LAYOUT_GROUP_HEADER_PADDING = 56;

export const STEP_LAYOUT_GROUP_MIN_WIDTH = 240;
export const STEP_LAYOUT_GROUP_MIN_HEIGHT = 160;
/**
 * Side of the square box created from the canvas context menu. Square because
 * an empty group carries no hint about which way its contents will grow, and a
 * square reads as "a region to fill" rather than a row or a column.
 */
export const STEP_LAYOUT_GROUP_DEFAULT_SIZE = 400;

/** Fallbacks for a node React Flow hasn't measured yet (first render, off-screen). */
const FALLBACK_NODE_WIDTH = 200;
const FALLBACK_NODE_HEIGHT = 120;

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Editor callbacks injected into group node data by the same decoration pass
 * that wires up `StepNodeActionCallbacks`. Absent in read-only views, which is
 * what makes the label and colour picker non-interactive there.
 */
export interface StepLayoutGroupNodeCallbacks {
  onRenameStepLayoutGroup?: (groupId: string, label: string) => void;
  onRecolorStepLayoutGroup?: (
    groupId: string,
    color: StepLayoutGroupColor | null,
  ) => void;
  /**
   * Fired when a resize gesture finishes, with the box's final rect. Stretching
   * a group over more steps has to adopt them, or the box would show them
   * enclosed without grouping them.
   */
  onResizeStepLayoutGroupEnd?: (groupId: string, rect: Rect) => void;
}

export interface StepLayoutGroupNodeData
  extends StepLayoutGroupNodeCallbacks, Record<string, unknown> {
  /** The `StepLayoutGroup.id` this node renders. Equal to the node id. */
  groupId: string;
  label: string;
  /** Explicit colour, or `null` to take `derivedColor` instead. */
  color: StepLayoutGroupColor | null;
  /**
   * The members' shared colour, or null when they disagree. Computed by the
   * editors' decoration pass (see `deriveStepLayoutGroupColorsById`) rather than
   * inside the node, so it costs one pass over the graph instead of one per
   * group per store update.
   */
  derivedColor?: StepLayoutGroupColor | null;
  /** Set in the workflow-group editor so frame-scoped helpers can find it. */
  workflowId?: string;
  isReadOnly?: boolean;
  /**
   * A group has no sockets. These are present-and-empty rather than absent so a
   * group node structurally satisfies `StepNodeData` and can live in the
   * editor's existing `useNodesState<StepNode>` list — the alternative is a
   * union node type threaded through every `node.data.inputs` read in a
   * 6k-line editor. Code that means "the steps" filters on
   * `type !== STEP_LAYOUT_GROUP_NODE_TYPE` (see `toAbsoluteStepNodes`); code that
   * merely walks sockets sees nothing and no-ops.
   */
  inputs: Record<string, never>;
  outputs: Record<string, never>;
}

export type StepLayoutGroupNode = Node<
  StepLayoutGroupNodeData,
  typeof STEP_LAYOUT_GROUP_NODE_TYPE
>;

export function isStepLayoutGroupNode(node: Node): node is StepLayoutGroupNode {
  return node.type === STEP_LAYOUT_GROUP_NODE_TYPE;
}

function getNodeWidth(node: Node): number {
  return (
    node.measured?.width ??
    node.width ??
    (typeof node.style?.width === 'number'
      ? node.style.width
      : FALLBACK_NODE_WIDTH)
  );
}

function getNodeHeight(node: Node): number {
  return (
    node.measured?.height ??
    node.height ??
    (typeof node.style?.height === 'number'
      ? node.style.height
      : FALLBACK_NODE_HEIGHT)
  );
}

/** A group node's rect, reading the dimensions React Flow stores in `style`. */
export function getStepLayoutGroupRect(node: Node): Rect {
  return {
    x: node.position.x,
    y: node.position.y,
    width: getNodeWidth(node),
    height: getNodeHeight(node),
  };
}

export function getNodeRect(node: Node): Rect {
  return {
    x: node.position.x,
    y: node.position.y,
    width: getNodeWidth(node),
    height: getNodeHeight(node),
  };
}

function containsRect(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

/**
 * Derive a group's tint from its members: the shared palette family when every
 * member has one, otherwise `null` (the caller falls back to neutral).
 *
 * Keyed on the palette family rather than the category name on purpose —
 * several categories are deliberately the same colour (Data Transform and
 * DataSource Adapter are both violet), and to the eye those *are* one header
 * colour, which is the thing being matched.
 */
export function deriveStepLayoutGroupColor(
  memberNodes: Array<{ data?: { category?: unknown } }>,
): StepLayoutGroupColor | null {
  let shared: StepLayoutGroupColor | null = null;
  let sawMember = false;

  for (const node of memberNodes) {
    const category = node.data?.category;
    if (typeof category !== 'string' || category.length === 0) return null;
    const { colorKey } = getCategoryStyle(category);
    if (!sawMember) {
      shared = colorKey;
      sawMember = true;
    } else if (shared !== colorKey) {
      return null;
    }
  }

  return sawMember ? shared : null;
}

/**
 * Derive every group's auto-colour in a single pass over the node list.
 *
 * Done here, once, rather than from a store selector inside each group node:
 * that scanned all nodes per group on every store tick, so a drag over a large
 * graph cost O(nodes x groups) per frame. The editors already map the node list
 * once per render to decorate it, so this rides along for free.
 */
export function deriveStepLayoutGroupColorsById(
  nodes: Node[],
): Map<string, StepLayoutGroupColor | null> {
  const membersByGroupId = new Map<
    string,
    Array<{ data?: { category?: unknown } }>
  >();
  for (const node of nodes) {
    if (isStepLayoutGroupNode(node)) {
      if (!membersByGroupId.has(node.id)) membersByGroupId.set(node.id, []);
      continue;
    }
    if (!node.parentId) continue;
    const members = membersByGroupId.get(node.parentId);
    if (members) members.push(node);
    else membersByGroupId.set(node.parentId, [node]);
  }

  const colorByGroupId = new Map<string, StepLayoutGroupColor | null>();
  for (const node of nodes) {
    if (!isStepLayoutGroupNode(node)) continue;
    colorByGroupId.set(
      node.id,
      deriveStepLayoutGroupColor(membersByGroupId.get(node.id) ?? []),
    );
  }
  return colorByGroupId;
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/**
 * The smallest group rect that encloses the given nodes with padding. Used both
 * when grouping a selection and when a group needs to grow to keep a member it
 * already owns.
 */
export function getBoundingRectForNodes(nodes: Node[]): Rect | null {
  if (nodes.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxRight = -Infinity;
  let maxBottom = -Infinity;

  for (const node of nodes) {
    const rect = getNodeRect(node);
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxRight = Math.max(maxRight, rect.x + rect.width);
    maxBottom = Math.max(maxBottom, rect.y + rect.height);
  }

  return {
    x: Math.round(minX - STEP_LAYOUT_GROUP_PADDING),
    y: Math.round(minY - STEP_LAYOUT_GROUP_HEADER_PADDING),
    width: Math.max(
      STEP_LAYOUT_GROUP_MIN_WIDTH,
      Math.round(maxRight - minX + STEP_LAYOUT_GROUP_PADDING * 2),
    ),
    height: Math.max(
      STEP_LAYOUT_GROUP_MIN_HEIGHT,
      Math.round(
        maxBottom -
          minY +
          STEP_LAYOUT_GROUP_HEADER_PADDING +
          STEP_LAYOUT_GROUP_PADDING,
      ),
    ),
  };
}

/**
 * The group a node has been dropped fully inside, or `null`.
 *
 * Containment is total, not overlap: a node straddling a group's edge stays
 * out. Partial overlap would make membership depend on sub-pixel drag
 * positions, and a step silently joining a group it's only half inside is worse
 * than having to drag it a bit further.
 *
 * Innermost wins when groups overlap, so dropping into a small group nested
 * inside a large one does what it looks like.
 */
export function findEnclosingGroupId(
  nodeRect: Rect,
  groupRects: Array<{ id: string; rect: Rect }>,
): string | null {
  let bestId: string | null = null;
  let bestArea = Infinity;

  for (const { id, rect } of groupRects) {
    if (!containsRect(rect, nodeRect)) continue;
    const area = rect.width * rect.height;
    if (area < bestArea) {
      bestArea = area;
      bestId = id;
    }
  }

  return bestId;
}

// ---------------------------------------------------------------------------
// Graph <-> definition
// ---------------------------------------------------------------------------

function buildStepLayoutGroupNode(
  group: StepLayoutGroup,
  options?: { workflowId?: string; isReadOnly?: boolean },
): StepLayoutGroupNode {
  return {
    id: group.id,
    type: STEP_LAYOUT_GROUP_NODE_TYPE,
    position: { x: group.position.x, y: group.position.y },
    // Deliberately no `zIndex`: React Flow spreads `node.style` *after* its own
    // computed `zIndex`, so setting one here overrides RF's stacking, and a
    // negative value drops the node behind `.react-flow__pane`, which then
    // swallows every pointer event on it. Groups stay behind by paint order
    // instead — emitted before the step nodes, with members elevated as children.
    //
    // `pointerEvents: 'none'` (which *does* want to override RF, and is applied
    // by the same style spread) makes the box's interior transparent to the
    // canvas. A backdrop spanning a big region would otherwise intercept every
    // pane gesture that starts inside it: shift-drag lasso, canvas panning, and
    // right-click. Only the label pill and the resize handles opt back in with
    // `pointer-events-auto`, which is also what makes "drag the box by its title"
    // the single, honest way to move it.
    style: {
      width: group.size.width,
      height: group.size.height,
      pointerEvents: 'none' as const,
    },
    data: {
      groupId: group.id,
      label: group.label,
      color: group.color ?? null,
      workflowId: options?.workflowId,
      isReadOnly: options?.isReadOnly,
      inputs: {},
      outputs: {},
    },
    // The label pill and colour picker live inside the node; dragging is
    // handled through an explicit drag handle so a click on the body pans the
    // canvas instead of hauling the whole region around by accident.
    dragHandle: '.step-layout-group-drag-handle',
  };
}

/**
 * Splice group nodes into a workflow's node list and reparent their members.
 *
 * Returns groups first so that, for the nodes React Flow can't order by nesting
 * (an ungrouped step overlapping a group), the paint order still puts the
 * backdrop behind.
 */
export function buildStepLayoutGroupGraph(
  stepNodes: Node[],
  stepLayoutGroups: StepLayoutGroup[],
  options?: { workflowId?: string; isReadOnly?: boolean },
): Node[] {
  if (stepLayoutGroups.length === 0) return stepNodes;

  const groupById = new Map(stepLayoutGroups.map((group) => [group.id, group]));
  const groupIdByStepId = new Map<string, string>();
  for (const group of stepLayoutGroups) {
    for (const stepId of group.stepIds) {
      groupIdByStepId.set(stepId, group.id);
    }
  }

  // Outermost first. React Flow needs a parent adopted before its children, and
  // emitting in depth order also keeps the paint order right for nested boxes.
  const depthOf = (group: StepLayoutGroup): number => {
    let depth = 0;
    let parent = group.parentGroupId
      ? groupById.get(group.parentGroupId)
      : undefined;
    const seen = new Set<string>([group.id]);
    while (parent && !seen.has(parent.id)) {
      depth += 1;
      seen.add(parent.id);
      parent = parent.parentGroupId
        ? groupById.get(parent.parentGroupId)
        : undefined;
    }
    return depth;
  };
  const ordered = [...stepLayoutGroups].sort((a, b) => depthOf(a) - depthOf(b));

  const groupNodes = ordered.map((group) => {
    const node = buildStepLayoutGroupNode(group, options);
    const parent = group.parentGroupId
      ? groupById.get(group.parentGroupId)
      : undefined;
    if (!parent) return node;
    // Persisted positions are workflow-absolute at every level, so a nested box
    // only needs its immediate parent's origin removed — the parent has already
    // been placed relative to *its* parent.
    return {
      ...node,
      parentId: parent.id,
      position: {
        x: group.position.x - parent.position.x,
        y: group.position.y - parent.position.y,
      },
    };
  });

  const childNodes = stepNodes.map((node) => {
    const groupId = groupIdByStepId.get(node.id);
    const group = groupId ? groupById.get(groupId) : undefined;
    if (!group) return node;

    return {
      ...node,
      parentId: group.id,
      position: {
        x: node.position.x - group.position.x,
        y: node.position.y - group.position.y,
      },
    };
  });

  return [...groupNodes, ...childNodes];
}

/**
 * Workflow-absolute position of every node, walking the parent chain.
 *
 * A node whose `parentId` isn't in `nodes` contributes no offset — that's how the
 * workflow-group editor works, where step nodes are parented to a workflow frame
 * that has already been removed from the list and de-padded by the caller.
 */
type PositionedNode = Pick<Node, 'id' | 'position' | 'parentId'>;

export function getAbsolutePositions(
  nodes: readonly PositionedNode[],
): Map<string, XYPosition> {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const resolved = new Map<string, XYPosition>();

  function resolve(node: PositionedNode, inProgress: Set<string>): XYPosition {
    const cached = resolved.get(node.id);
    if (cached) return cached;
    // Defensive: a cycle can't be built through the editor, but resolving one
    // must not recurse forever.
    if (inProgress.has(node.id)) return node.position;
    inProgress.add(node.id);

    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    const base = parent ? resolve(parent, inProgress) : { x: 0, y: 0 };
    const absolute = {
      x: node.position.x + base.x,
      y: node.position.y + base.y,
    };
    resolved.set(node.id, absolute);
    return absolute;
  }

  for (const node of nodes) resolve(node, new Set());
  return resolved;
}

/**
 * Read the current step layout groups back out of a React Flow node list.
 *
 * Membership comes from live `parentId` rather than the stored `stepIds`, so
 * drag-in, drag-out, and delete all persist through the same path with no
 * separate bookkeeping to fall out of sync.
 */
export function extractStepLayoutGroups(nodes: Node[]): StepLayoutGroup[] {
  const groupNodes = nodes.filter(isStepLayoutGroupNode);
  if (groupNodes.length === 0) return [];

  const groupIds = new Set(groupNodes.map((node) => node.id));
  const stepIdsByGroupId = new Map<string, string[]>(
    groupNodes.map((node) => [node.id, []]),
  );
  for (const node of nodes) {
    if (isStepLayoutGroupNode(node) || !node.parentId) continue;
    stepIdsByGroupId.get(node.parentId)?.push(node.id);
  }

  const absoluteById = getAbsolutePositions(nodes);

  return groupNodes.map((node) => {
    const rect = getStepLayoutGroupRect(node);
    const absolute = absoluteById.get(node.id) ?? node.position;
    // A group's parent is another group only when the parent is one; in the
    // workflow-group editor it's the workflow frame instead, which is not part
    // of this concept.
    const parentGroupId =
      node.parentId && groupIds.has(node.parentId) ? node.parentId : null;

    return {
      id: node.id,
      label: node.data.label,
      position: { x: Math.round(absolute.x), y: Math.round(absolute.y) },
      size: {
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      },
      color: node.data.color,
      stepIds: stepIdsByGroupId.get(node.id) ?? [],
      parentGroupId,
    };
  });
}

/**
 * Strip group nesting from a node list: drop the group nodes and restore every
 * member's absolute position. The result is what the rest of the editor (and
 * `graphToDefinition`) expects — a flat list of steps in workflow coordinates.
 */
export function toAbsoluteStepNodes(nodes: Node[]): Node[] {
  // One pass: the boxes to unwind through and the steps to unwind are the two
  // halves of the same partition.
  const groupIds = new Set<string>();
  const stepNodes: Node[] = [];
  for (const node of nodes) {
    if (isStepLayoutGroupNode(node)) groupIds.add(node.id);
    else stepNodes.push(node);
  }
  if (groupIds.size === 0) return stepNodes;

  // Walks the full chain, so a step inside a nested box unwinds through every
  // enclosing box rather than just its immediate one.
  const absoluteById = getAbsolutePositions(nodes);

  return stepNodes.map((node) => {
    if (!node.parentId || !groupIds.has(node.parentId)) return node;
    const absolute = absoluteById.get(node.id);
    if (!absolute) return node;
    const { parentId: _parentId, ...rest } = node;
    return { ...rest, position: absolute };
  });
}

/**
 * Re-derive group boxes around wherever their members ended up.
 *
 * Auto-layout moves steps without any notion of groups, which would leave every
 * box stranded over empty canvas. Rather than teaching ELK about groups (it has
 * hierarchy support, but using it would make group geometry a layout *input*
 * and fight the user's hand-drawn boxes on every re-layout), groups follow: each
 * one shrink-wraps its members again afterwards. A group whose members all
 * vanished keeps its previous geometry so an empty labelled box isn't silently
 * resized to nothing.
 */
export function refitStepLayoutGroupsAroundMembers(
  groups: StepLayoutGroup[],
  absoluteStepNodes: Node[],
): StepLayoutGroup[] {
  const nodeById = new Map(absoluteStepNodes.map((node) => [node.id, node]));
  const childGroupsByParent = new Map<string, StepLayoutGroup[]>();
  for (const group of groups) {
    const parentId = group.parentGroupId ?? null;
    if (parentId === null) continue;
    const siblings = childGroupsByParent.get(parentId);
    if (siblings) siblings.push(group);
    else childGroupsByParent.set(parentId, [group]);
  }

  // Innermost first: a box encloses its steps *and* the boxes nested in it, so
  // the inner ones have to have settled before the outer one measures them.
  const fitted = new Map<string, StepLayoutGroup>();
  function fit(
    group: StepLayoutGroup,
    inProgress: Set<string>,
  ): StepLayoutGroup {
    const done = fitted.get(group.id);
    if (done) return done;
    if (inProgress.has(group.id)) return group;
    inProgress.add(group.id);

    const children: Node[] = group.stepIds
      .map((stepId) => nodeById.get(stepId))
      .filter((node): node is Node => node !== undefined);

    for (const child of childGroupsByParent.get(group.id) ?? []) {
      const fittedChild = fit(child, inProgress);
      children.push({
        id: fittedChild.id,
        position: fittedChild.position,
        measured: {
          width: fittedChild.size.width,
          height: fittedChild.size.height,
        },
        data: {},
      } as Node);
    }

    const rect = getBoundingRectForNodes(children);
    const next = rect
      ? {
          ...group,
          position: { x: rect.x, y: rect.y },
          size: { width: rect.width, height: rect.height },
        }
      : group;
    fitted.set(group.id, next);
    return next;
  }

  return groups.map((group) => fit(group, new Set()));
}

/**
 * Convert a node's position into the coordinate space of `groupId` (or out of
 * any group when `groupId` is null), preserving where it sits on screen.
 * The single place reparenting arithmetic happens.
 */
export function reparentNode(
  node: Node,
  groupId: string | null,
  groupPositionById: Map<string, XYPosition>,
): Node {
  const fromPosition = node.parentId
    ? groupPositionById.get(node.parentId)
    : undefined;
  const toPosition = groupId ? groupPositionById.get(groupId) : undefined;

  const absolute = {
    x: node.position.x + (fromPosition?.x ?? 0),
    y: node.position.y + (fromPosition?.y ?? 0),
  };
  const position = {
    x: absolute.x - (toPosition?.x ?? 0),
    y: absolute.y - (toPosition?.y ?? 0),
  };

  if (groupId === null) {
    const { parentId: _parentId, ...rest } = node;
    return { ...rest, position };
  }
  return { ...node, parentId: groupId, position };
}

/**
 * Every layout box with its rect in flow coordinates, innermost-comparable.
 *
 * Group nodes store positions relative to their parent (another box, or a
 * workflow frame in the group editor), so a hit test against a raw canvas point
 * needs them resolved to absolute first.
 */
export function getStepLayoutGroupFlowRects(
  nodes: Node[],
): Array<{ id: string; rect: Rect; label: string }> {
  const absoluteById = getAbsolutePositions(nodes);
  const rects: Array<{ id: string; rect: Rect; label: string }> = [];
  for (const node of nodes) {
    if (!isStepLayoutGroupNode(node)) continue;
    const absolute = absoluteById.get(node.id) ?? node.position;
    const rect = getStepLayoutGroupRect(node);
    rects.push({
      id: node.id,
      rect: { ...rect, x: absolute.x, y: absolute.y },
      label: node.data.label,
    });
  }
  return rects;
}

/**
 * The selected nodes that aren't already covered by a selected ancestor.
 *
 * This is what "how many things are selected" means once boxes nest: selecting a
 * box selects it as *one* entity, not as itself plus however many steps happen to
 * be inside it. Lassoing a box and one loose step should read as two things and
 * group into a box containing a box and a step — so the steps inside the selected
 * box are filtered out here rather than counted.
 */
export function getStepLayoutSelectionRoots<TNode extends Node>(
  nodes: TNode[],
): TNode[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const selectedIds = new Set<string>();
  for (const node of nodes) {
    if (node.selected) selectedIds.add(node.id);
  }

  const hasSelectedAncestor = (node: TNode): boolean => {
    const seen = new Set<string>([node.id]);
    let parentId = node.parentId;
    while (parentId && !seen.has(parentId)) {
      if (selectedIds.has(parentId)) return true;
      seen.add(parentId);
      parentId = byId.get(parentId)?.parentId;
    }
    return false;
  };

  return nodes.filter((node) => node.selected && !hasSelectedAncestor(node));
}

/**
 * Re-derive which group (if any) each candidate step belongs to, from geometry
 * alone, and reparent the ones whose answer changed.
 *
 * Membership is a pure function of "is this step fully inside that box", so the
 * same routine backs every gesture that can change the answer — dragging a step,
 * resizing a box, and drawing a new one. Anything else drifts: a box you
 * stretched over a node would show it enclosed but not group it.
 *
 * `groupRectOverrides` supplies authoritative geometry for a group whose resize
 * is still settling into React Flow's measured dimensions, so the decision uses
 * the rect the user actually dragged rather than a stale one.
 */
export function reassignStepLayoutGroupMembership<TNode extends Node>(
  nodes: TNode[],
  candidateStepIds: Set<string>,
  groupRectOverrides?: Map<string, Rect>,
): TNode[] {
  if (candidateStepIds.size === 0) return nodes;

  const groupNodes = nodes.filter(isStepLayoutGroupNode);
  if (groupNodes.length === 0) return nodes;

  const rectFor = (group: Node): Rect =>
    groupRectOverrides?.get(group.id) ?? getStepLayoutGroupRect(group);

  const groupPositionById = new Map<string, XYPosition>(
    groupNodes.map((group) => {
      const rect = rectFor(group);
      return [group.id, { x: rect.x, y: rect.y }];
    }),
  );
  const groupById = new Map(groupNodes.map((group) => [group.id, group]));

  // A group may itself be dropped into another group, so descendants have to be
  // excluded as targets — parenting a box into its own child would build a cycle
  // that no coordinate walk could resolve.
  const descendantsOf = (groupId: string): Set<string> => {
    const out = new Set<string>();
    const queue = [groupId];
    while (queue.length > 0) {
      const current = queue.pop();
      for (const group of groupNodes) {
        if (group.parentId === current && !out.has(group.id)) {
          out.add(group.id);
          queue.push(group.id);
        }
      }
    }
    return out;
  };

  let changed = false;
  const next = nodes.map((node) => {
    if (!candidateStepIds.has(node.id)) return node;

    // Candidates are compared in the space their candidate groups live in: if
    // the step is currently inside a group, that's the group's own parent space.
    const currentGroup = node.parentId
      ? groupById.get(node.parentId)
      : undefined;
    const siblingParentId =
      (currentGroup ? currentGroup.parentId : node.parentId) ?? null;

    const rect = getNodeRect(node);
    const currentGroupPosition = currentGroup
      ? groupPositionById.get(currentGroup.id)
      : undefined;
    const searchRect = currentGroupPosition
      ? {
          ...rect,
          x: rect.x + currentGroupPosition.x,
          y: rect.y + currentGroupPosition.y,
        }
      : rect;

    const excluded = isStepLayoutGroupNode(node)
      ? new Set<string>([node.id, ...descendantsOf(node.id)])
      : new Set<string>();

    const candidates: Array<{ id: string; rect: Rect }> = [];
    for (const group of groupNodes) {
      if ((group.parentId ?? null) !== siblingParentId) continue;
      if (excluded.has(group.id)) continue;
      candidates.push({ id: group.id, rect: rectFor(group) });
    }

    const targetGroupId = findEnclosingGroupId(searchRect, candidates);
    if ((node.parentId ?? null) === (targetGroupId ?? null)) return node;

    changed = true;
    const reparented = reparentNode(
      node,
      targetGroupId,
      groupPositionById,
    ) as TNode;

    // Leaving a group means re-homing onto the group's own parent — the workflow
    // frame in the group editor, nothing in the plain editor.
    if (targetGroupId === null && currentGroup?.parentId) {
      return { ...reparented, parentId: currentGroup.parentId } as TNode;
    }
    return reparented;
  });

  return changed ? next : nodes;
}

/**
 * Remove the given boxes and lift their children into whatever the box's own
 * parent was — the inverse of grouping.
 *
 * Pure, and lives here rather than inside `useStepLayoutGroups` because it
 * depends on nothing but its arguments; keeping it in the hook rebuilt it every
 * render for no reason. Shared by explicit deletion and the keyboard-delete
 * guard: in both cases the steps must survive, since a box is a label and
 * deleting a label is not deleting the work.
 */
export function dissolveStepLayoutGroups<TNode extends Node>(
  nodes: TNode[],
  groupIds: Set<string>,
): TNode[] {
  const groupById = new Map<string, TNode>();
  for (const node of nodes) {
    if (isStepLayoutGroupNode(node)) groupById.set(node.id, node);
  }

  const kept: TNode[] = [];
  for (const node of nodes) {
    if (groupIds.has(node.id)) continue;

    if (!node.parentId || !groupIds.has(node.parentId)) {
      kept.push(node);
      continue;
    }

    // Climb until an ancestor that survives, accumulating each removed box's
    // origin as we go. Re-homing one level is not enough: deleting an outer box
    // expands to the boxes nested in it, so a member's immediate parent is
    // frequently being removed too — which would leave it pointing at a node
    // that no longer exists (React Flow errors) and offset by every box the walk
    // skipped.
    let offsetX = 0;
    let offsetY = 0;
    let survivingParentId: string | undefined = node.parentId;
    const visited = new Set<string>();

    while (survivingParentId && groupIds.has(survivingParentId)) {
      if (visited.has(survivingParentId)) {
        survivingParentId = undefined;
        break;
      }
      visited.add(survivingParentId);

      const ancestor = groupById.get(survivingParentId);
      if (!ancestor) {
        // Being deleted but not in this list — nothing left to re-home onto.
        survivingParentId = undefined;
        break;
      }
      offsetX += ancestor.position.x;
      offsetY += ancestor.position.y;
      survivingParentId = ancestor.parentId;
    }

    const { parentId: _parentId, ...rest } = node;
    kept.push({
      ...rest,
      // Whatever the walk landed on: another box, a workflow frame, or nothing.
      ...(survivingParentId ? { parentId: survivingParentId } : {}),
      position: {
        x: node.position.x + offsetX,
        y: node.position.y + offsetY,
      },
    } as TNode);
  }
  return kept;
}

/**
 * Narrow a React Flow deletion set so removing a group box removes only the box.
 *
 * React Flow expands a parent deletion to its children *and* to every edge
 * attached to them, so a naive pass-through would delete a box's steps and
 * silently rewire the graph around them. A group must never have any functional
 * effect on a workflow, so both are undone here: members are dropped from the
 * node set, and any edge touching a member is dropped from the edge set.
 *
 * A step that was itself selected alongside its group survives. React Flow gives
 * no way to tell "the user picked this step" apart from "we expanded to it", and
 * losing work to that ambiguity is worse than leaving an extra node behind.
 */
export function planStepLayoutGroupDeletion<
  TNode extends Node,
  TEdge extends Edge,
>(
  nodesToDelete: TNode[],
  edgesToDelete: TEdge[],
): { nodes: TNode[]; edges: TEdge[]; groupIds: Set<string> } {
  const groupIds = new Set<string>();
  for (const node of nodesToDelete) {
    if (isStepLayoutGroupNode(node)) groupIds.add(node.id);
  }
  if (groupIds.size === 0) {
    return { nodes: nodesToDelete, edges: edgesToDelete, groupIds };
  }

  const keptMemberIds = new Set<string>();
  for (const node of nodesToDelete) {
    if (
      !isStepLayoutGroupNode(node) &&
      node.parentId !== undefined &&
      groupIds.has(node.parentId)
    ) {
      keptMemberIds.add(node.id);
    }
  }

  return {
    nodes: nodesToDelete.filter((node) => !keptMemberIds.has(node.id)),
    // Edges between two genuinely-deleted steps still go; anything touching a
    // step we're keeping does not.
    edges: edgesToDelete.filter(
      (edge) =>
        !keptMemberIds.has(edge.source) && !keptMemberIds.has(edge.target),
    ),
    groupIds,
  };
}

/**
 * Every node that could plausibly change membership when `groupId`'s box moves
 * or resizes: its current children, plus whatever shares its parent space that a
 * bigger box could swallow. Includes sibling *groups*, since boxes nest.
 */
export function getMembershipCandidatesForStepLayoutGroup(
  nodes: Node[],
  groupId: string,
): Set<string> {
  const group = nodes.find(
    (node) => node.id === groupId && isStepLayoutGroupNode(node),
  );
  if (!group) return new Set();

  const siblingParentId = group.parentId ?? null;
  const candidates = new Set<string>();
  for (const node of nodes) {
    if (
      node.id !== groupId &&
      (node.parentId === groupId || (node.parentId ?? null) === siblingParentId)
    ) {
      candidates.add(node.id);
    }
  }
  return candidates;
}
